import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';

const PHASE_SELECT = '*, deliverables(*, deliverable_items(*))';

/**
 * 排序 phase 內的 deliverables 和 items
 */
const sortPhaseChildren = (phases) =>
  (phases || []).map((phase) => ({
    ...phase,
    deliverables: (phase.deliverables || [])
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((d) => ({
        ...d,
        deliverable_items: (d.deliverable_items || []).sort((a, b) => a.sort_order - b.sort_order),
      })),
  }));

/**
 * 查詢指定專案的所有 Phases（含巢狀 deliverables + items）
 */
export const getPhasesByProject = async (projectId) => {
  try {
    const { data, error } = await supabase
      .from('phases')
      .select(PHASE_SELECT)
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return { data: sortPhaseChildren(data), error: null };
  } catch (error) {
    console.error('Error fetching phases:', error);
    return { data: null, error };
  }
};

/**
 * 查詢全公司所有 Phases + Deliverables（Milestone 視圖用）
 */
export const getAllPhases = async ({ companyId } = {}) => {
  try {
    let query = supabase
      .from('phases')
      .select(PHASE_SELECT)
      .order('sort_order', { ascending: true });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: sortPhaseChildren(data), error: null };
  } catch (error) {
    console.error('Error fetching all phases:', error);
    return { data: null, error };
  }
};

/**
 * 建立新 Phase
 */
export const createPhase = async (data) => {
  try {
    const insertData = {
      project_id: data.project_id,
      company_id: data.company_id || null,
      name: data.name,
      sort_order: data.sort_order ?? 0,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      status: data.status || 'active',
    };

    const { data: phase, error } = await supabase
      .from('phases')
      .insert(insertData)
      .select(PHASE_SELECT)
      .single();

    if (error) throw error;

    await logAudit('create', 'phase', phase.id, phase.name, {
      after: phase,
    });

    return { data: phase, error: null };
  } catch (error) {
    console.error('Error creating phase:', error);
    return { data: null, error };
  }
};

/**
 * 更新 Phase
 */
export const updatePhase = async (phaseId, updates) => {
  try {
    const { data: beforeData } = await supabase
      .from('phases')
      .select('*')
      .eq('id', phaseId)
      .single();

    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.start_date !== undefined) updateData.start_date = updates.start_date || null;
    if (updates.end_date !== undefined) updateData.end_date = updates.end_date || null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.sort_order !== undefined) updateData.sort_order = updates.sort_order;
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('phases')
      .update(updateData)
      .eq('id', phaseId)
      .select(PHASE_SELECT)
      .single();

    if (error) throw error;

    await logAudit('update', 'phase', phaseId, data.name, {
      before: beforeData,
      after: data,
    });

    return { data, error: null };
  } catch (error) {
    console.error('Error updating phase:', error);
    return { data: null, error };
  }
};

/**
 * 刪除 Phase（CASCADE 會連帶刪除 deliverables）
 */
export const deletePhase = async (phaseId) => {
  try {
    const { data: phaseData } = await supabase
      .from('phases')
      .select('name, project_id, company_id')
      .eq('id', phaseId)
      .single();

    const { error } = await supabase
      .from('phases')
      .delete()
      .eq('id', phaseId);

    if (error) throw error;

    await logAudit('delete', 'phase', phaseId, phaseData?.name, {
      before: phaseData,
    });

    return { error: null };
  } catch (error) {
    console.error('Error deleting phase:', error);
    return { error };
  }
};
